import { SUPABASE_URL } from '@/config';
import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

const CACHE_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

export type GoogleAdsConnectionStatus = {
  isConnected: boolean;
  customerId: string | null;
  customerName: string | null;
};

export type GoogleAdsCustomer = {
  id: string;
  name: string;
};

export type GoogleAdsCampaign = {
  campaignId: string;
  campaignName: string;
  status: string;
  campaignType: string | null;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  conversionValue: number;
  cpc: number;
  cpa: number;
  ctr: number;
  cvr: number;
};

export type GoogleAdsOverview = {
  totalImpressions: number;
  totalClicks: number;
  totalCost: number;
  totalConversions: number;
  totalConversionValue: number;
  avgCpc: number;
  avgCpa: number;
  avgCtr: number;
  avgCvr: number;
  campaigns: GoogleAdsCampaign[];
  lastSyncedAt: string | null;
};

async function invoke(body: Record<string, any>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const res = await fetch(`${SUPABASE_URL}/functions/v1/google-ads`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
    },
    body: JSON.stringify({ ...body, userId: session?.user?.id, organizationId: getActiveOrgId() }),
  });

  const text = await res.text();
  let data: any;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(
      `Google Ads edge function returned non-JSON (status ${res.status}). Is the function deployed?`
    );
  }
  if (!res.ok) throw new Error(data?.error || `Edge function error ${res.status}`);
  if (data?.error) throw new Error(data.error);
  return data;
}

function buildOverview(rows: any[], lastSyncedAt: string | null): GoogleAdsOverview {
  // Aggregate by campaign
  const campaignMap = new Map<string, {
    campaignName: string;
    status: string;
    campaignType: string | null;
    impressions: number;
    clicks: number;
    costMicros: number;
    conversions: number;
    conversionValue: number;
  }>();

  for (const row of rows) {
    const key = row.campaign_id;
    const existing = campaignMap.get(key);
    if (existing) {
      existing.impressions += row.impressions;
      existing.clicks += row.clicks;
      existing.costMicros += Number(row.cost_micros);
      existing.conversions += Number(row.conversions);
      existing.conversionValue += Number(row.conversion_value);
    } else {
      campaignMap.set(key, {
        campaignName: row.campaign_name,
        status: row.status,
        campaignType: row.campaign_type,
        impressions: row.impressions,
        clicks: row.clicks,
        costMicros: Number(row.cost_micros),
        conversions: Number(row.conversions),
        conversionValue: Number(row.conversion_value),
      });
    }
  }

  let totalImpressions = 0;
  let totalClicks = 0;
  let totalCostMicros = 0;
  let totalConversions = 0;
  let totalConversionValue = 0;

  const campaigns: GoogleAdsCampaign[] = [];

  campaignMap.forEach((v, campaignId) => {
    const cost = v.costMicros / 1_000_000;
    totalImpressions += v.impressions;
    totalClicks += v.clicks;
    totalCostMicros += v.costMicros;
    totalConversions += v.conversions;
    totalConversionValue += v.conversionValue;

    campaigns.push({
      campaignId,
      campaignName: v.campaignName,
      status: v.status,
      campaignType: v.campaignType,
      impressions: v.impressions,
      clicks: v.clicks,
      cost,
      conversions: v.conversions,
      conversionValue: v.conversionValue,
      cpc: v.clicks > 0 ? cost / v.clicks : 0,
      cpa: v.conversions > 0 ? cost / v.conversions : 0,
      ctr: v.impressions > 0 ? (v.clicks / v.impressions) * 100 : 0,
      cvr: v.clicks > 0 ? (v.conversions / v.clicks) * 100 : 0,
    });
  });

  // Sort by cost descending
  campaigns.sort((a, b) => b.cost - a.cost);

  const totalCost = totalCostMicros / 1_000_000;

  return {
    totalImpressions,
    totalClicks,
    totalCost,
    totalConversions,
    totalConversionValue,
    avgCpc: totalClicks > 0 ? totalCost / totalClicks : 0,
    avgCpa: totalConversions > 0 ? totalCost / totalConversions : 0,
    avgCtr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
    avgCvr: totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0,
    campaigns,
    lastSyncedAt,
  };
}

const googleAdsService = {
  async getConnectionStatus(): Promise<GoogleAdsConnectionStatus> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { isConnected: false, customerId: null, customerName: null };

    const orgId = getActiveOrgId();
    let query = supabase
      .from('google_ads_connections')
      .select('customer_id, customer_name')
      .eq('user_id', user.id);

    if (orgId) query = query.eq('organization_id', orgId);

    const { data } = await query.maybeSingle();

    return {
      isConnected: !!data,
      customerId: data?.customer_id ?? null,
      customerName: data?.customer_name ?? null,
    };
  },

  async listCustomers(): Promise<GoogleAdsCustomer[]> {
    const result = await invoke({ action: 'listCustomers' });
    return result.customers ?? [];
  },

  async saveCustomer(customerId: string, customerName: string): Promise<void> {
    await invoke({ action: 'saveCustomer', customerId, customerName });
  },

  async disconnect(): Promise<void> {
    await invoke({ action: 'disconnect' });
  },

  async sync(startDate?: string, endDate?: string): Promise<void> {
    await invoke({ action: 'sync', startDate, endDate });
  },

  async testConnection(): Promise<any> {
    return invoke({ action: 'test' });
  },

  async getOverview(startDate: string, endDate: string): Promise<GoogleAdsOverview> {
    // Get connected customer ID
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return buildOverview([], null);

    const orgId = getActiveOrgId();
    let connQuery = supabase
      .from('google_ads_connections')
      .select('customer_id')
      .eq('user_id', user.id);

    if (orgId) connQuery = connQuery.eq('organization_id', orgId);

    const { data: conn } = await connQuery.maybeSingle();

    if (!conn) return buildOverview([], null);

    // Read from cache
    const { data: rows } = await supabase
      .from('google_ads_campaign_cache')
      .select('*')
      .eq('customer_id', conn.customer_id)
      .gte('date', startDate)
      .lte('date', endDate);

    const lastSyncedAt = rows?.[0]?.synced_at ?? null;

    // Auto-sync if stale
    const isStale =
      !lastSyncedAt || Date.now() - new Date(lastSyncedAt).getTime() > CACHE_MAX_AGE_MS;

    if (isStale) {
      try {
        await invoke({ action: 'sync', startDate, endDate });
        const { data: freshRows } = await supabase
          .from('google_ads_campaign_cache')
          .select('*')
          .eq('customer_id', conn.customer_id)
          .gte('date', startDate)
          .lte('date', endDate);

        return buildOverview(freshRows ?? [], freshRows?.[0]?.synced_at ?? null);
      } catch {
        return buildOverview(rows ?? [], lastSyncedAt);
      }
    }

    return buildOverview(rows ?? [], lastSyncedAt);
  },
};

export default googleAdsService;
